import React from 'react';

function ariaSortValue(sortConfig, sortKey) {
  if (!sortKey || sortConfig?.key !== sortKey) return 'none';
  return sortConfig.direction === 'asc' ? 'ascending' : 'descending';
}

/**
 * Shared colgroup + thead for user-resizable data tables.
 *
 * Column def shape:
 * { key, label?, sortKey?, resizable?, defaultWidth, minWidth?, maxWidth?, thClassName?, centerHeader?,
 *   headerContent?, renderHeader?(ctx), sortButtonClassName?, sortIconClassName? }
 */
function columnWidthStyle(widths, col) {
  const raw = widths?.[col.key];
  const n = Number.isFinite(Number(raw)) ? Math.round(Number(raw)) : Number(col.defaultWidth);
  if (!Number.isFinite(n)) return undefined;
  const widthPx = `${n}px`;
  return { width: widthPx, minWidth: widthPx, maxWidth: widthPx };
}

export function ResizableColgroup({ columns, widths }) {
  return (
    <colgroup>
      {columns.map((col) => {
        const style = columnWidthStyle(widths, col);
        return (
          <col
            key={col.key}
            data-col-key={col.key}
            // HTML width attr helps sticky thead keep the same layout as tbody in Chromium
            width={style ? parseInt(style.width, 10) : undefined}
            style={style}
            className={col.colClassName}
          />
        );
      })}
    </colgroup>
  );
}

export function ResizableTableHead({
  columns,
  widths,
  onResizeStart,
  resizingKey,
  sortConfig,
  onSort,
  getSortIcon,
  innerClassName = 'assets-th-inner',
  labelClassName = 'assets-th-label',
  resizeHandleClassName = 'assets-col-resize-handle',
  defaultSortButtonClassName = 'assets-sort-btn',
  defaultSortIconClassName = 'assets-sort-icon',
}) {
  return (
    <thead>
      <tr>
        {columns.map((col, index) => {
          const thStyle = widths ? columnWidthStyle(widths, col) : undefined;
          const showResizeHandle =
            Boolean(onResizeStart) && col.resizable !== false && index < columns.length - 1;

          if (typeof col.renderHeader === 'function') {
            return (
              <th
                key={col.key}
                scope="col"
                className={col.thClassName}
                style={thStyle}
                aria-sort={col.sortKey ? ariaSortValue(sortConfig, col.sortKey) : undefined}
              >
                <div className={`${innerClassName}${col.centerHeader ? ` ${innerClassName}--center` : ''}`}>
                  {col.renderHeader({
                    col,
                    sortConfig,
                    onSort,
                    getSortIcon,
                    onResizeStart,
                    resizingKey,
                    showResizeHandle,
                    innerClassName,
                    labelClassName,
                    resizeHandleClassName,
                    defaultSortButtonClassName,
                    defaultSortIconClassName,
                  })}
                </div>
              </th>
            );
          }

          const sortBtnClass = col.sortButtonClassName || defaultSortButtonClassName;
          const sortIconClass = col.sortIconClassName || defaultSortIconClassName;

          return (
            <th
              key={col.key}
              scope="col"
              className={col.thClassName}
              style={thStyle}
              aria-sort={col.sortKey ? ariaSortValue(sortConfig, col.sortKey) : undefined}
            >
              <div className={`${innerClassName}${col.centerHeader ? ` ${innerClassName}--center` : ''}`}>
                {col.headerContent ? (
                  col.headerContent
                ) : col.sortKey && onSort ? (
                  <button type="button" className={sortBtnClass} onClick={() => onSort(col.sortKey)}>
                    {col.label}{' '}
                    <span className={`${sortIconClass} ${sortConfig?.key === col.sortKey ? 'is-active' : ''}`}>
                      {getSortIcon ? getSortIcon(col.sortKey) : '↕'}
                    </span>
                  </button>
                ) : (
                  <span className={labelClassName}>{col.label}</span>
                )}
                {showResizeHandle ? (
                  <span
                    className={`${resizeHandleClassName}${resizingKey === col.key ? ' is-active' : ''}`}
                    role="separator"
                    aria-orientation="vertical"
                    aria-label={`Resize ${col.label || col.key} column`}
                    onMouseDown={(e) => onResizeStart(col.key, e)}
                  />
                ) : null}
              </div>
            </th>
          );
        })}
      </tr>
    </thead>
  );
}
